/**
 * semantic-message-dictionary.ts
 *
 * Canonical Typed Semantic i18n Dictionary and Fallback Resolver (Sprint CMS-KIOSK-THEME-MEDIA-01).
 *
 * Invariants:
 * - Structured dot-notated TranslationKeys ensuring zero hardcoded English dictionary keys.
 * - Compile-time exhaustiveness: all keys are verified across enabled locales (en-US, sw-KE, lg-UG, fr-FR).
 * - Fallback precedence: requestedLocale -> tenantFallback -> 'en-US' -> key fallback.
 */

export const EN_US_MESSAGES = {
  'kiosk.catalog.all_categories': 'All Products',
  'kiosk.product.add_to_tray': 'Add to Tray',
  'kiosk.product.added_to_tray': 'Added to Tray',
  'kiosk.product.out_of_stock': 'Out of Stock',
  'kiosk.product.show_item_qr': 'Show item QR',
  'kiosk.product.back_to_photo': 'Back to photo',
  'kiosk.product.price_label': 'Price',
  'kiosk.checkout.order_successful': 'Order Accepted',
  'kiosk.checkout.order_reference': 'Order Reference',
  'kiosk.checkout.total_paid': 'Total Paid',
  'kiosk.checkout.tender_method': 'Tender Method',
  'kiosk.checkout.claim_code_label': 'Claim Code',
  'kiosk.checkout.copy_code': 'Copy Code',
  'kiosk.checkout.copied': 'Copied',
  'kiosk.checkout.thermal_receipt': 'Print Thermal Receipt',
  'kiosk.checkout.complete_reset': 'Complete & Reset',
  'cms.hero.tagline': 'Operational Across Kampala, Entebbe & Extraction Corridors',
  'cms.hero.heading': 'Protecting Critical Assets. Securing Uganda’s Future.',
  'cms.hero.subheadline': 'From Bank-Grade Private Vault Custody to Armored Bullion Logistics and 24/7 AI-Assisted Surveillance.',
  'cms.hero.cta_assessment': 'Request Security Assessment',
  'cms.hero.cta_vaults': 'Explore Private Vaults',
  'cms.pillar.custody': 'Secure Custody & Private Vaults',
  'cms.pillar.cit': 'Cash-in-Transit (CIT) Logistics',
  'cms.pillar.soc': 'AI Surveillance & Central SOC',
  'cms.pillar.industry': 'Industrial & Mining Concessions',
  'cms.pillar.audit': 'Threat Assessment & Auditing',
  'cms.form.title': 'Initiate Confidential Security Consultation',
  'cms.form.submit': 'Transmit Confidential Request',
} as const;

export type TranslationKey = keyof typeof EN_US_MESSAGES;

export const SW_KE_MESSAGES: Record<TranslationKey, string> = {
  'kiosk.catalog.all_categories': 'Bidhaa Zote',
  'kiosk.product.add_to_tray': 'Weka Kwenye Sinia',
  'kiosk.product.added_to_tray': 'Imewekwa Kwenye Sinia',
  'kiosk.product.out_of_stock': 'Imeisha Mtandaoni',
  'kiosk.product.show_item_qr': 'Onyesha QR ya bidhaa',
  'kiosk.product.back_to_photo': 'Rudi kwenye picha',
  'kiosk.product.price_label': 'Bei',
  'kiosk.checkout.order_successful': 'Agizo Limekubaliwa',
  'kiosk.checkout.order_reference': 'Nambari ya Agizo',
  'kiosk.checkout.total_paid': 'Jumla Iliyolipwa',
  'kiosk.checkout.tender_method': 'Njia ya Malipo',
  'kiosk.checkout.claim_code_label': 'Msimbo wa Kudai',
  'kiosk.checkout.copy_code': 'Nakili Msimbo',
  'kiosk.checkout.copied': 'Imenakiliwa',
  'kiosk.checkout.thermal_receipt': 'Chapa Risiti',
  'kiosk.checkout.complete_reset': 'Kamilisha na Ufute',
  'cms.hero.tagline': 'Inafanya Kazi Kampala, Entebbe na Kanda za Uchimbaji Madini',
  'cms.hero.heading': 'Kulinda Rasilimali Muhimu. Kulinda Mustakabali wa Uganda.',
  'cms.hero.subheadline': 'Kuanzia Hifadhi ya Vyumba vya Siri vya Benki hadi Usafirishaji wa Dhahabu na Ulinzi wa AI wa Saa 24.',
  'cms.hero.cta_assessment': 'Omba Tathmini ya Usalama',
  'cms.hero.cta_vaults': 'Gundua Vyumba vya Siri',
  'cms.pillar.custody': 'Ulinzi Salama wa Hati na Vyumba vya Siri',
  'cms.pillar.cit': 'Usafirishaji wa Fedha na Dhahabu (CIT)',
  'cms.pillar.soc': 'Ufuatiliaji wa Kamera za AI na Kituo Kikuu cha SOC',
  'cms.pillar.industry': 'Ulinzi wa Viwanda na Migodi',
  'cms.pillar.audit': 'Tathmini ya Hatari na Ukaguzi wa Usalama',
  'cms.form.title': 'Anzisha Mashauriano ya Siri ya Usalama',
  'cms.form.submit': 'Tuma Ombi la Siri',
};

export const LG_UG_MESSAGES: Record<TranslationKey, string> = {
  'kiosk.catalog.all_categories': 'Eby’Obugagga Byonna',
  'kiosk.product.add_to_tray': 'Teeka mu Kasawo',
  'kiosk.product.added_to_tray': 'Kiteekeddwaamu',
  'kiosk.product.out_of_stock': 'Biweddeeyo',
  'kiosk.product.show_item_qr': 'Laga QR y’ekintu',
  'kiosk.product.back_to_photo': 'Ddayo ku kifaananyi',
  'kiosk.product.price_label': 'Omuwendo',
  'kiosk.checkout.order_successful': 'Ekinaaguzi Kikkiriziddwa',
  'kiosk.checkout.order_reference': 'Ennamba y’Ekinaaguzi',
  'kiosk.checkout.total_paid': 'Omugatte Ogusasuddwa',
  'kiosk.checkout.tender_method': 'Engeri y’Okusasulamu',
  'kiosk.checkout.claim_code_label': 'Koodi y’Okunona',
  'kiosk.checkout.copy_code': 'Koppa Koodi',
  'kiosk.checkout.copied': 'Bikoppebbwa',
  'kiosk.checkout.thermal_receipt': 'Fulumya Lisiiti',
  'kiosk.checkout.complete_reset': 'Maliriza & Ddamu Buto',
  'cms.hero.tagline': 'Tukolera mu Kampala, Entebbe n’Ebyalo Ebisimibwamu Eby’obugagga',
  'cms.hero.heading': 'Okukuuma Eby’Obugagga Ebikulu. Okutebenkeza Ebiseera by’e Ggwanga.',
  'cms.hero.subheadline': 'Okuva ku Masanduuko g’Ebyama ag’omulembe okutuuka ku Byuma ebitambuza Sente ne Zaabu n’Obukuumi bwa AI obwa Saawa 24.',
  'cms.hero.cta_assessment': 'Saba Okwekenneenya Obukuumi',
  'cms.hero.cta_vaults': 'Zuula Amasanduuko g’Ebyama',
  'cms.pillar.custody': 'Okutereka Ebyapa n’Amasanduuko g’Ebyama',
  'cms.pillar.cit': 'Emmotoka Ezitambuza Sente ne Zaabu (CIT)',
  'cms.pillar.soc': 'Kamera za AI n’Ekitebe Ekikessi ekya SOC',
  'cms.pillar.industry': 'Obukuumi bw’Ebyuma Ebinene n’Eby’Obugagga',
  'cms.pillar.audit': 'Okwekenneenya Obuzibu n’Obukuumi',
  'cms.form.title': 'Tandika Okwebuuza ku By’Obukuumi mu Kyama',
  'cms.form.submit': 'Weereza Okusaba Kw’Ekyama',
};

export const FR_FR_MESSAGES: Record<TranslationKey, string> = {
  'kiosk.catalog.all_categories': 'Tous les Produits',
  'kiosk.product.add_to_tray': 'Ajouter au plateau',
  'kiosk.product.added_to_tray': 'Ajouté au plateau',
  'kiosk.product.out_of_stock': 'Rupture de stock',
  'kiosk.product.show_item_qr': 'Afficher le QR de l’article',
  'kiosk.product.back_to_photo': 'Retour à la photo',
  'kiosk.product.price_label': 'Prix',
  'kiosk.checkout.order_successful': 'Commande Acceptée',
  'kiosk.checkout.order_reference': 'Référence Commande',
  'kiosk.checkout.total_paid': 'Total Payé',
  'kiosk.checkout.tender_method': 'Mode de Paiement',
  'kiosk.checkout.claim_code_label': 'Code de Réclamation',
  'kiosk.checkout.copy_code': 'Copier le Code',
  'kiosk.checkout.copied': 'Copié',
  'kiosk.checkout.thermal_receipt': 'Imprimer Reçu Thermique',
  'kiosk.checkout.complete_reset': 'Terminer et Réinitialiser',
  'cms.hero.tagline': 'Opérationnel à Kampala, Entebbe et dans les Corridors Miniers',
  'cms.hero.heading': 'Protéger les Actifs Critiques. Sécuriser l’Avenir de l’Ouganda.',
  'cms.hero.subheadline': 'Des coffres-forts privés ultra-sécurisés au transport de fonds blindé et à la surveillance IA 24/7.',
  'cms.hero.cta_assessment': 'Demander une Évaluation',
  'cms.hero.cta_vaults': 'Explorer les Coffres Privés',
  'cms.pillar.custody': 'Coffres-Forts et Garde d’Actifs',
  'cms.pillar.cit': 'Transport de Fonds et Métaux Précieux (CIT)',
  'cms.pillar.soc': 'Surveillance IA et Centre Opérationnel (SOC)',
  'cms.pillar.industry': 'Défense Industrielle et Minière',
  'cms.pillar.audit': 'Évaluation des Risques et Audits',
  'cms.form.title': 'Initier une Consultation Confidentielle',
  'cms.form.submit': 'Transmettre la Demande Confidentielle',
};

export const AR_SA_MESSAGES: Record<TranslationKey, string> = {

  'kiosk.catalog.all_categories': 'جميع المنتجات',
  'kiosk.product.add_to_tray': 'إضافة إلى السلة',
  'kiosk.product.added_to_tray': 'تمت الإضافة إلى السلة',
  'kiosk.product.out_of_stock': 'غير متوفر في المخزون',
  'kiosk.product.show_item_qr': 'عرض رمز الاستجابة السريعة QR',
  'kiosk.product.back_to_photo': 'الرجوع إلى الصورة',
  'kiosk.product.price_label': 'السعر',
  'kiosk.checkout.order_successful': 'تم قبول الطلب',
  'kiosk.checkout.order_reference': 'مرجع الطلب',
  'kiosk.checkout.total_paid': 'الإجمالي المدفوع',
  'kiosk.checkout.tender_method': 'طريقة الدفع',
  'kiosk.checkout.claim_code_label': 'رمز الاستلام',
  'kiosk.checkout.copy_code': 'نسخ الرمز',
  'kiosk.checkout.copied': 'تم النسخ',
  'kiosk.checkout.thermal_receipt': 'طباعة الإيصال الحراري',
  'kiosk.checkout.complete_reset': 'إنهاء وإعادة ضبط',
  'cms.hero.tagline': 'نعمل في كمبالا وعنتيبي وممرات التعدين الحيوية',
  'cms.hero.heading': 'حماية الأصول الحيوية. تأمين مستقبل أوغندا.',
  'cms.hero.subheadline': 'من خزائن الودائع المصرفية الخاصة وفائقة الأمان إلى لوجستيات السبائك المصفحة وحراسة الذكاء الاصطناعي على مدار الساعة.',
  'cms.hero.cta_assessment': 'طلب تقييم أمني',
  'cms.hero.cta_vaults': 'استكشاف الخزائن الخاصة',
  'cms.pillar.custody': 'الحفظ الآمن والخزائن الخاصة',
  'cms.pillar.cit': 'نقل الأموال والسبائك المصفح (CIT)',
  'cms.pillar.soc': 'المراقبة بالذكاء الاصطناعي ومركز العمليات (SOC)',
  'cms.pillar.industry': 'أمن القطاعات الصناعية والتعدين',
  'cms.pillar.audit': 'تقييم المخاطر والاستشارات الأمنية',
  'cms.form.title': 'بدء استشارة أمنية سرية',
  'cms.form.submit': 'إرسال الطلب السري',
};

export const ZH_CN_MESSAGES: Record<TranslationKey, string> = {
  'kiosk.catalog.all_categories': '所有产品',
  'kiosk.product.add_to_tray': '加入托盘',
  'kiosk.product.added_to_tray': '已加入托盘',
  'kiosk.product.out_of_stock': '暂无库存',
  'kiosk.product.show_item_qr': '显示商品二维码',
  'kiosk.product.back_to_photo': '返回商品图片',
  'kiosk.product.price_label': '价格',
  'kiosk.checkout.order_successful': '订单已接受',
  'kiosk.checkout.order_reference': '订单参考号',
  'kiosk.checkout.total_paid': '已付总额',
  'kiosk.checkout.tender_method': '支付方式',
  'kiosk.checkout.claim_code_label': '取件码',
  'kiosk.checkout.copy_code': '复制代码',
  'kiosk.checkout.copied': '已复制',
  'kiosk.checkout.thermal_receipt': '打印热敏收据',
  'kiosk.checkout.complete_reset': '完成并重置',
  'cms.hero.tagline': '服务覆盖坎帕拉、恩德培及重点采矿走廊',
  'cms.hero.heading': '守护关键资产，捍卫乌干达经济未来。',
  'cms.hero.subheadline': '从银行级私人高安金库保管、装甲贵金属与现钞押运，到24/7全天候人工智能安防指挥中心。',
  'cms.hero.cta_assessment': '申请安全评估',
  'cms.hero.cta_vaults': '了解私人金库',
  'cms.pillar.custody': '安全托管与私人金库',
  'cms.pillar.cit': '武装押运与贵金属物流 (CIT)',
  'cms.pillar.soc': 'AI智能监控与集中指挥中心 (SOC)',
  'cms.pillar.industry': '重大工业与矿产特许权安防',
  'cms.pillar.audit': '威胁评估与安全审计咨询',
  'cms.form.title': '发起保密安全咨询',
  'cms.form.submit': '发送保密申请',
};

export const CANONICAL_I18N_DICTIONARIES: Record<string, Record<TranslationKey, string>> = {
  'en-US': EN_US_MESSAGES,
  'sw-KE': SW_KE_MESSAGES,
  'lg-UG': LG_UG_MESSAGES,
  'fr-FR': FR_FR_MESSAGES,
  'ar-SA': AR_SA_MESSAGES,
  'zh-CN': ZH_CN_MESSAGES,
};


export function resolveTranslation(
  key: TranslationKey,
  locale: string,
  tenantFallback = 'en-US'
): string {
  return (
    CANONICAL_I18N_DICTIONARIES[locale]?.[key] ??
    CANONICAL_I18N_DICTIONARIES[tenantFallback]?.[key] ??
    CANONICAL_I18N_DICTIONARIES['en-US'][key] ??
    key
  );
}
